"use client";

import { useEffect, useRef, type ReactNode } from "react";

type Props = {
  /** Page Builder `data-parallax-speed` (jarallax semantics: 0..2, 1 = no movement). */
  speed: number;
  /** Background-image class generated by the parser (desktop/mobile media rules). */
  imageClassName?: string;
  /** Video background rendered inside the moving layer instead of an image. */
  children?: ReactNode;
};

/**
 * Replacement for jarallax 2.0.3 used by Magento (row `data-enable-parallax`).
 * The layer is absolutely positioned behind the row content; its media is
 * taller than the row and translated on scroll so it moves at `speed` × the
 * scroll velocity. `background-size/position/repeat` are inherited from the
 * row, so the Page Builder style block stays the single source of truth.
 *
 * Without JS, with `prefers-reduced-motion`, or for `speed === 1` the media
 * simply covers the row — identical to a static background.
 */
export function PbParallaxLayer({ speed, imageClassName, children }: Props) {
  const layerRef = useRef<HTMLDivElement>(null);
  const mediaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const layer = layerRef.current;
    const media = mediaRef.current;
    if (!layer || !media) return;
    const s = Math.min(2, Math.max(-1, Number.isFinite(speed) ? speed : 0.5));
    if (s === 1 || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let frame = 0;
    let visible = false;

    const update = () => {
      frame = 0;
      const rect = layer.getBoundingClientRect();
      const vh = window.innerHeight;
      if (rect.bottom < 0 || rect.top > vh || rect.height === 0) return;
      const range = Math.abs(1 - s) * (vh + rect.height);
      const progress = Math.min(1, Math.max(0, (vh - rect.top) / (vh + rect.height)));
      const offset = s < 1 ? -range * (1 - progress) : -range * progress;
      media.style.height = `${rect.height + range}px`;
      media.style.transform = `translate3d(0, ${offset.toFixed(2)}px, 0)`;
    };

    const schedule = () => {
      if (visible && !frame) frame = window.requestAnimationFrame(update);
    };

    const io = new IntersectionObserver(
      (entries) => {
        visible = entries.some((e) => e.isIntersecting);
        schedule();
      },
      { rootMargin: "100px 0px" }
    );
    io.observe(layer);
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule, { passive: true });
    layer.dataset.pbParallax = "active";

    return () => {
      io.disconnect();
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      if (frame) window.cancelAnimationFrame(frame);
      media.style.height = "";
      media.style.transform = "";
      delete layer.dataset.pbParallax;
    };
  }, [speed]);

  return (
    <div ref={layerRef} className="pb-bg-layer" aria-hidden="true">
      <div ref={mediaRef} className={imageClassName ? `pb-bg-layer__media ${imageClassName}` : "pb-bg-layer__media"}>
        {children}
      </div>
    </div>
  );
}
